import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton as MuiIconButton,
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { Switch } from '../switch/Switch';
import { IconButton } from '../icon-button/IconButton';
import { dialogActionsSx, dialogButtonSx } from '../../core/theme/buttonStyles';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandle, SORT_TRANSITION } from '../tables/draggableList';

export interface VariantItem {
  name: string;
  size: string;
  price: string;
}

// Klucz tylko do identyfikacji wiersza przy przeciąganiu (dnd-kit) — nie jest
// wysyłany do backendu, kolejność wynika z pozycji w tablicy przy zapisie.
type IndexedItem = VariantItem & { _key: string };

export interface VariantGroup {
  key: string;
  name: string;
  isRequired: boolean;
  isSingleChoice: boolean;
  items: VariantItem[];
}

interface ItemErrors {
  name?: string;
  price?: string;
}

interface GroupErrors {
  name?: string;
  items?: string;
  rows?: Record<number, ItemErrors>;
}

const emptyItem = (): VariantItem => ({ name: '', size: '', price: '' });

// Wiersz uznany za "zaczęty" przez uzytkownika. W pełni puste, końcowe wiersze
// są po prostu ignorowane, a nie traktowane jako błąd.
const isMeaningful = (i: VariantItem): boolean =>
  Boolean(i.name.trim() || i.price.trim() || i.size.trim());

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
  },
  '& .MuiInputBase-input': { fontSize: 14, padding: '10px 12px' },
};

const SortableItemRow = ({
  item,
  rowErr,
  disableRemove,
  onChange,
  onRemove,
}: {
  item: IndexedItem;
  rowErr?: ItemErrors;
  disableRemove: boolean;
  onChange: (field: keyof VariantItem, value: string) => void;
  onRemove: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: item._key,
    transition: SORT_TRANSITION,
  });

  return (
    <Stack
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform) }}
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        transition: `${SORT_TRANSITION.duration}ms ${SORT_TRANSITION.easing}`,
        ...(isDragging ? { opacity: 0.5 } : {}),
      }}
    >
      <Box sx={{ mt: 'var(--space-1)' }}>
        <DragHandle listeners={listeners} attributes={attributes} />
      </Box>
      <TextField
        size="small"
        sx={{ ...inputSx, flex: 2 }}
        placeholder={t('locationsManagement.createItem.name')}
        value={item.name}
        onChange={(e) => onChange('name', e.target.value)}
        error={Boolean(rowErr?.name)}
        helperText={rowErr?.name || ' '}
        slotProps={{ htmlInput: { maxLength: 400 } }}
      />
      <TextField
        size="small"
        sx={{ ...inputSx, flex: 1 }}
        placeholder={t('locationsManagement.createItem.amount')}
        value={item.size}
        onChange={(e) => onChange('size', e.target.value)}
        helperText=" "
        slotProps={{ htmlInput: { maxLength: 400 } }}
      />
      <TextField
        size="small"
        sx={{ ...inputSx, flex: 1 }}
        placeholder={t('locationsManagement.createItem.price')}
        value={item.price}
        onChange={(e) => onChange('price', e.target.value)}
        error={Boolean(rowErr?.price)}
        helperText={rowErr?.price || ' '}
        slotProps={{ htmlInput: { maxLength: 400 } }}
      />
      <IconButton
        icon={<DeleteOutlineIcon fontSize="small" />}
        ariaLabel={t('common.delete')}
        color="var(--color-error)"
        onClick={onRemove}
        sx={{
          mt: 'var(--space-1)',
          opacity: disableRemove ? 0.4 : 1,
          pointerEvents: disableRemove ? 'none' : 'auto',
        }}
      />
    </Stack>
  );
};

// Szablon to zapisana do ponownego uzycia grupa wariantow (per lokalizacja).
export interface VariantTemplate {
  id: number;
  locationId: number;
  name: string;
  isRequired: boolean;
  isSingleChoice: boolean;
  items: VariantItem[];
}

export interface VariantGroupSaveOptions {
  saveAsTemplate: boolean;
}

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (group: VariantGroup, options: VariantGroupSaveOptions) => void;
  initialGroup?: VariantGroup | null;
  makeKey: () => string;
  /** Szablony do szybkiego startu — pokazywane tylko przy dodawaniu nowej grupy. */
  templates?: VariantTemplate[];
  /** Checkbox "Zapisz tez jako szablon" w stopce. */
  allowSaveAsTemplate?: boolean;
  /** Tryb edycji samego szablonu (ekran "Szablony wariantow") — inne tytuly i przycisk. */
  templateMode?: boolean;
};

export const VariantGroupDialog: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  initialGroup,
  makeKey,
  templates = [],
  allowSaveAsTemplate = false,
  templateMode = false,
}) => {
  const { t } = useTranslation();
  const [saveAsTemplate, setSaveAsTemplate] = React.useState(false);
  const [appliedTemplateId, setAppliedTemplateId] = React.useState<number | null>(null);

  const itemKeyRef = React.useRef(0);
  const makeItemKey = () => `i${++itemKeyRef.current}`;

  const [name, setName] = React.useState('');
  const [isSingleChoice, setIsSingleChoice] = React.useState(true);
  const [isRequired, setIsRequired] = React.useState(false);
  const [items, setItems] = React.useState<IndexedItem[]>(() => [{ ...emptyItem(), _key: makeItemKey() }]);
  const [errors, setErrors] = React.useState<GroupErrors>({});

  const itemSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  // Wypelnij formularz za kazdym razem, gdy dialog sie otwiera (od zera albo
  // z istniejacej grupy).
  React.useEffect(() => {
    if (!open) return;
    setName(initialGroup?.name ?? '');
    setIsSingleChoice(initialGroup?.isSingleChoice ?? true);
    setIsRequired(initialGroup?.isRequired ?? false);
    setItems(
      initialGroup?.items?.length
        ? initialGroup.items.map((i) => ({ ...i, _key: makeItemKey() }))
        : [{ ...emptyItem(), _key: makeItemKey() }],
    );
    setErrors({});
    setSaveAsTemplate(false);
    setAppliedTemplateId(null);
  }, [open, initialGroup]);

  // Szablon wypelnia formularz kopia — dalsze zmiany dotycza tylko tej grupy.
  const applyTemplate = (template: VariantTemplate) => {
    setName(template.name);
    setIsSingleChoice(template.isSingleChoice);
    setIsRequired(template.isRequired);
    setItems(
      template.items.length
        ? template.items.map((i) => ({ ...i, _key: makeItemKey() }))
        : [{ ...emptyItem(), _key: makeItemKey() }],
    );
    setErrors({});
    setAppliedTemplateId(template.id);
  };

  const updateItem = (key: string, field: keyof VariantItem, value: string) => {
    setItems((prev) => prev.map((it) => (it._key === key ? { ...it, [field]: value } : it)));
  };

  const addItemRow = () => setItems((prev) => [...prev, { ...emptyItem(), _key: makeItemKey() }]);

  const removeItemRow = (key: string) => {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((it) => it._key !== key)));
  };

  const handleItemDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((it) => it._key === active.id);
      const newIndex = prev.findIndex((it) => it._key === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const validate = (): GroupErrors => {
    const next: GroupErrors = {};
    if (!name.trim()) {
      next.name = t('locationsManagement.createItem.variantGroupDialog.groupNameRequiredError');
    }

    if (items.filter(isMeaningful).length === 0) {
      next.items = t('locationsManagement.createItem.variantGroupDialog.atLeastOneItemError');
    }

    const rows: Record<number, ItemErrors> = {};
    items.forEach((it, i) => {
      const rowErr: ItemErrors = {};
      if (!isMeaningful(it)) return; // w pelni puste, koncowe wiersze nie sa bledem
      if (!it.name.trim()) {
        rowErr.name = t('locationsManagement.createItem.variantGroupDialog.itemNameRequiredError');
      }
      const priceNum = Number(it.price);
      if (it.price.trim() === '' || Number.isNaN(priceNum) || priceNum < 0) {
        rowErr.price = t('locationsManagement.createItem.variantGroupDialog.itemPriceRequiredError');
      }
      if (Object.keys(rowErr).length) rows[i] = rowErr;
    });
    if (Object.keys(rows).length) next.rows = rows;
    return next;
  };

  const handleSave = () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;

    const cleanedItems = items
      .filter(isMeaningful)
      .map((i) => ({
        name: i.name.trim(),
        size: i.size.trim(),
        price: i.price.trim(),
      }));

    onSave(
      {
        key: initialGroup?.key ?? makeKey(),
        name: name.trim(),
        isRequired,
        isSingleChoice,
        items: cleanedItems,
      },
      { saveAsTemplate: allowSaveAsTemplate && saveAsTemplate },
    );
    onClose();
  };

  const isEdit = Boolean(initialGroup);
  const showTemplates = !isEdit && !templateMode && templates.length > 0;
  const dialogKeys = 'locationsManagement.createItem.variantGroupDialog';
  const title = templateMode
    ? t(isEdit ? 'variantTemplates.editTemplate' : 'variantTemplates.addTemplate')
    : t(isEdit ? `${dialogKeys}.editVariantGroup` : `${dialogKeys}.addVariantGroup`);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
        paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {title}
        <MuiIconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </MuiIconButton>
      </DialogTitle>

      <DialogContent dividers>
        {showTemplates && (
          <Box sx={{ mb: 'var(--space-4)' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'text.secondary', mb: 1 }}>
              {t('variantTemplates.startFromTemplate')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {templates.map((template) => {
                const selected = template.id === appliedTemplateId;
                return (
                  <Box
                    key={template.id}
                    component="button"
                    type="button"
                    onClick={() => applyTemplate(template)}
                    aria-pressed={selected}
                    title={template.items.map((i) => i.name).join(', ')}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 'var(--space-1)',
                      px: 'var(--space-3)',
                      py: 'var(--space-1)',
                      borderRadius: 'var(--radius-pill)',
                      border: '1px solid',
                      borderColor: selected ? 'var(--color-primary)' : 'var(--color-secondary)',
                      backgroundColor: selected ? 'var(--color-primary-soft)' : 'var(--color-surface)',
                      color: selected ? 'var(--color-primary-active)' : 'var(--color-text-primary)',
                      fontFamily: 'inherit',
                      fontSize: 'var(--text-sm)',
                      cursor: 'pointer',
                      transition: 'background-color var(--dur-fast) var(--ease-standard)',
                      '&:hover': { borderColor: 'var(--color-primary)' },
                    }}
                  >
                    {template.name}
                    <Box component="span" sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                      · {t('variantTemplates.itemsCount', { count: template.items.length })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary', mt: 1 }}>
              {t('variantTemplates.startFromTemplateHint')}
            </Typography>
          </Box>
        )}

        {/* Ustawienia na poziomie grupy */}
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary', mb: 0.5 }}>
              {t('locationsManagement.createItem.variantGroupDialog.groupName')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              sx={inputSx}
              placeholder={t('locationsManagement.createItem.variantGroupDialog.groupNamePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={Boolean(errors.name)}
              helperText={errors.name || ' '}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
          </Box>

          <FormControl>
            <Typography id="variant-choice-type" sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary', mb: 0.5 }}>
              {t('locationsManagement.createItem.variantGroupDialog.choiceType')}
            </Typography>
            {/* "Wymagane" stoi w tym samym rzedzie co radia i ma ten sam uklad:
                kontrolka + etykieta obok. Wczesniej osobny blok z etykieta po lewej
                byl centrowany wzgledem calosci i lądowal miedzy podpisem a radiami. */}
            <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={4}>
              <RadioGroup
                row
                aria-labelledby="variant-choice-type"
                value={isSingleChoice ? 'single' : 'multiple'}
                onChange={(e) => setIsSingleChoice(e.target.value === 'single')}
              >
                <FormControlLabel
                  value="single"
                  control={<Radio size="small" />}
                  label={t('locationsManagement.createItem.variantGroupDialog.singleChoice')}
                />
                <FormControlLabel
                  value="multiple"
                  control={<Radio size="small" />}
                  label={t('locationsManagement.createItem.variantGroupDialog.multipleChoice')}
                />
              </RadioGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={isRequired}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsRequired(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={t('locationsManagement.createItem.variantGroupDialog.required')}
                sx={{ ml: 0, gap: 1.5 }}
              />
            </Stack>
          </FormControl>

          {/* Wiersze */}
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'text.secondary', mb: 1 }}>
              {t('locationsManagement.createItem.variantGroupDialog.items')}
            </Typography>

            <DndContext
              sensors={itemSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleItemDragEnd}
            >
              <SortableContext items={items.map((it) => it._key)} strategy={verticalListSortingStrategy}>
                <Stack spacing={1}>
                  {items.map((it, index) => (
                    <SortableItemRow
                      key={it._key}
                      item={it}
                      rowErr={errors.rows?.[index]}
                      disableRemove={items.length === 1}
                      onChange={(field, value) => updateItem(it._key, field, value)}
                      onRemove={() => removeItemRow(it._key)}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>

            {errors.items && <FormHelperText error>{errors.items}</FormHelperText>}

            <Button
              type="button"
              startIcon={<AddIcon />}
              onClick={addItemRow}
              sx={{ mt: 1, color: 'var(--color-primary)', textTransform: 'none' }}
            >
              {t('locationsManagement.createItem.variantGroupDialog.addItem')}
            </Button>
          </Box>
        </Stack>

        {allowSaveAsTemplate && !templateMode && (
          <Stack
            component="label"
            direction="row"
            alignItems="flex-start"
            spacing={1}
            sx={{ mt: 'var(--space-4)', cursor: 'pointer', width: 'fit-content' }}
          >
            <Checkbox
              size="small"
              checked={saveAsTemplate}
              onChange={(e) => setSaveAsTemplate(e.target.checked)}
              sx={{ p: 0, mt: '1px' }}
            />
            <Box>
              <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.primary' }}>
                {t('variantTemplates.saveAsTemplate')}
              </Typography>
              <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                {t('variantTemplates.saveAsTemplateHint')}
              </Typography>
            </Box>
          </Stack>
        )}

        {/* Stopka */}
        <Box sx={dialogActionsSx}>
          <Button variant="outlined" type="button" onClick={onClose} sx={dialogButtonSx}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" type="button" onClick={handleSave} sx={dialogButtonSx}>
            {templateMode ? t('variantTemplates.saveTemplate') : t(`${dialogKeys}.saveGroup`)}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
